import { Injectable, ChangeDetectorRef } from '@angular/core';
import {
  CoursePlayerService,
  CourseNavigationItem,
  CourseStructure,
  PlayerConfig
} from '../../../../services/course-player.service';

@Injectable()
export class LearnerNavigationService {
  constructor(
    private coursePlayerService: CoursePlayerService
  ) {}

  /**
   * Locates the synthetic `course_summary` nav item in the structure, if one
   * has been inserted. Summary insertion is owned by `LearnerProgressService`;
   * this method is the read-only "have we reached the summary yet" lookup
   * that the modal uses to jump there after the final module completes.
   */
  findCourseSummaryItem(courseStructure: CourseStructure | null): CourseNavigationItem | null {
    if (!courseStructure) return null;
    return courseStructure.navigationItems.find(
      item => item.type === 'course_summary'
    ) || null;
  }

  navigateToContent(
    courseStructure: CourseStructure,
    startItemId: string | undefined,
    isReturningLearner: boolean,
    courseProgressMap: Map<string, any>
  ): CourseNavigationItem | null {
    let startItem: CourseNavigationItem | null = null;

    if (startItemId) {
      startItem = courseStructure.navigationItems.find(
        item => item.id === startItemId
      ) || null;
    }

    if (!startItem) {
      if (isReturningLearner) {
        startItem = this.findNextIncompleteItem(courseStructure, courseProgressMap)
          || courseStructure.navigationItems[0];
      } else {
        startItem = courseStructure.navigationItems[0];
      }
    }

    return startItem;
  }

  findNextIncompleteItem(
    courseStructure: CourseStructure,
    courseProgressMap: Map<string, any>
  ): CourseNavigationItem | null {
    for (const item of courseStructure.navigationItems) {
      if (item.type === 'unit_intro') continue;

      const progressKey = this.coursePlayerService.getProgressKey(item);
      const progress = courseProgressMap.get(progressKey);

      // KAN-304: treat both completion flavors as complete (see canAccessItem).
      if (!progress || (!progress.moduleCompleted && !progress.completed)) {
        return item;
      }
    }

    return courseStructure.navigationItems[courseStructure.navigationItems.length - 1];
  }

  navigateToItem(
    item: CourseNavigationItem,
    courseStructure: CourseStructure,
    cdr: ChangeDetectorRef
  ): { currentItem: CourseNavigationItem; currentItemIndex: number } {
    const currentItemIndex = Math.max(0, courseStructure.navigationItems.indexOf(item));

    cdr.detectChanges();

    return { currentItem: item, currentItemIndex };
  }

  /**
   * KAN-323: whether a Prev/Next/back-arrow move to `item` is allowed. This is the
   * SINGLE gate for positional navigation and reuses the exact sidebar gate
   * (`canAccessItem`, the same one `onTimelineItemSelect` applies) plus the VS4
   * progression gate (KAN-318). Positional index math alone let learners walk
   * backward/forward into locked modules and quizzes.
   *
   * `isGateLocked` reports whether an item's unit is progression-locked (owned by
   * the modal's `computeGateLocks`). A gate-locked module/unit_quiz is unreachable;
   * a gate-locked `unit_intro` stays reachable so the learner can read WHY it is
   * locked (matches the always-clickable sidebar "Unit Overview").
   */
  canNavigateTo(
    item: CourseNavigationItem | null | undefined,
    playerConfig: PlayerConfig,
    courseStructure: CourseStructure,
    courseProgressMap: Map<string, any>,
    isGateLocked: (item: CourseNavigationItem) => boolean
  ): boolean {
    if (!item) return false;
    if (!this.canAccessItem(item, playerConfig, courseStructure, courseProgressMap)) {
      return false;
    }
    if ((item.type === 'module' || item.type === 'unit_quiz') && isGateLocked(item)) {
      return false;
    }
    return true;
  }

  updateNavigationState(
    courseStructure: CourseStructure | null,
    currentItemIndex: number,
    playerConfig: PlayerConfig,
    courseProgressMap: Map<string, any>,
    isGateLocked: (item: CourseNavigationItem) => boolean
  ): { canGoToPrevious: boolean; canGoToNext: boolean } {
    if (!courseStructure) {
      return { canGoToPrevious: false, canGoToNext: false };
    }
    const items = courseStructure.navigationItems;
    const prev = currentItemIndex > 0 ? items[currentItemIndex - 1] : null;
    const next = currentItemIndex < items.length - 1 ? items[currentItemIndex + 1] : null;
    return {
      canGoToPrevious: this.canNavigateTo(prev, playerConfig, courseStructure, courseProgressMap, isGateLocked),
      canGoToNext: this.canNavigateTo(next, playerConfig, courseStructure, courseProgressMap, isGateLocked)
    };
  }

  canAccessItem(
    item: CourseNavigationItem,
    playerConfig: PlayerConfig,
    courseStructure: CourseStructure,
    courseProgressMap: Map<string, any>
  ): boolean {
    if (!playerConfig.enforceSequential) {
      return true;
    }

    if (item.type === 'unit_intro') {
      return true;
    }

    const itemIndex = Math.max(0, courseStructure.navigationItems.indexOf(item));

    for (let i = 0; i < itemIndex; i++) {
      const prevItem = courseStructure.navigationItems[i];
      if (!prevItem || prevItem.type === 'unit_intro') continue;

      const progressKey = this.coursePlayerService.getProgressKey(prevItem);
      const progress = courseProgressMap.get(progressKey);
      // KAN-304: a predecessor counts as complete when EITHER `moduleCompleted`
      // or `completed` is set. Quiz-pass / unit-quiz completions land as
      // `completed: true` with `moduleCompleted` falsy, and the sidebar already
      // shows them with a green checkmark (see updateNavigationStatusesFromProgress).
      // Gating on `moduleCompleted` alone wrongly blocked navigation past those
      // already-unlocked items.
      if (!progress || (!progress.moduleCompleted && !progress.completed)) {
        return false;
      }
    }

    return true;
  }

  handleKeyDown(
    event: KeyboardEvent,
    canGoToPrevious: boolean,
    canGoToNext: boolean,
    onPrevious: () => void,
    onNext: () => void,
    onToggleTimeline: () => void,
    onClose: () => void
  ): void {
    const target = event.target as HTMLElement;
    const isInputField = target.tagName === 'INPUT' ||
                        target.tagName === 'TEXTAREA' ||
                        target.isContentEditable;

    if (event.key === 'Escape') {
      onClose();
      return;
    }

    if (isInputField) {
      return;
    }

    if (event.key === 'ArrowLeft' && canGoToPrevious) {
      onPrevious();
    } else if (event.key === 'ArrowRight' && canGoToNext) {
      onNext();
    } else if (event.key === 't' || event.key === 'T') {
      onToggleTimeline();
    }
  }
}
